import {ArrowLeft, Loader2, UserCheck} from "lucide-react";

const OTPField = ({
  value,
  onChange,
  isActionLoading,
  onAction,
  onResend,
  actionLabel,
  onBack,
  resendTimer,
  loginState,
}) => {
  const handleChange = (index, val) => {
    // Strip non-digits. Mobile Chrome SMS autofill can dump the whole code
    // into a single box at once, so `val` may be several digits, not one.
    const digits = val.replace(/[^0-9]/g, "");

    if (digits === "") {
      // Empty input = the box was cleared.
      const updatedArr = value.split("");
      updatedArr[index] = "";
      onChange(updatedArr.join(""));
      return;
    }

    // A single digit goes in the focused box; a multi-digit dump (autofill or
    // paste) is the whole code, so always lay it out from the first box --
    // even if the user happened to tap a later box before autofilling.
    const start = digits.length > 1 ? 0 : index;
    const updatedArr = value.split("");
    for (let k = 0; k < digits.length && start + k < 6; k++)
      updatedArr[start + k] = digits[k];
    const updatedStr = updatedArr.join("").slice(0, 6);
    onChange(updatedStr);

    const focusIdx = Math.min(start + digits.length, 5);
    document.getElementById(`otp-${focusIdx}`)?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/[^0-9]/g, "")
      .slice(0, 6);
    if (pastedData) {
      onChange(pastedData);
      const focusIdx = Math.min(pastedData.length, 5);
      document.getElementById(`otp-${focusIdx}`)?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !value[index] && index > 0)
      document.getElementById(`otp-${index - 1}`)?.focus();
  };

  // Real <form> + type="submit" so the mobile keyboard's Go/Enter key in a
  // code box verifies exactly like tapping the action button. The guard
  // mirrors the button's disabled state for Enter during an in-flight
  // request; Resend and Back must stay type="button" or they'd submit too.
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isActionLoading && value.length === 6) onAction();
  };

  return (
    <form
      className="w-full flex-1 flex flex-col items-center"
      onSubmit={handleSubmit}
      noValidate
    >
      {/* md:flex-1 only: on mobile the stretch would push the buttons to
          the bottom of the screen, behind the iOS keyboard. */}
      <div className="w-full max-w-md space-y-2 mb-6 md:flex-1 flex flex-col justify-start">
        <div>
          <label className="block text-[#191919] ml-1 mb-2">
            Verification code
          </label>
          <div className="flex justify-between gap-1 md:gap-2">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <input
                key={i}
                id={`otp-${i}`}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete={i === 0 ? "one-time-code" : "off"}
                autoFocus={i === 0}
                maxLength={6}
                value={value[i] || ""}
                onPaste={handlePaste}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, i)}
                style={{
                  WebkitTextFillColor: "transparent",
                  textShadow: value[i] ? "0 0 0 #374151" : "none",
                  fontSize: value[i] ? "27px" : "24px",
                  lineHeight: "1",
                }}
                className={`
                  w-12 h-12 md:w-16 md:h-12 text-center font-bold bg-slate-50 border-2 rounded-xl outline-none transition-all font-[urbanist] text-gray-700 shadow-sm
                  ${loginState.message_type === "error" ? "border-red-500 focus:border-red-600 focus:ring-red-100" : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-100"}
                  focus:bg-white focus:ring-4
                `}
              />
            ))}
          </div>
          <div className="flex justify-between items-center px-1 mt-4">
            <span className="text-gray-500">
              {resendTimer > 0 ? (
                <span>
                  Resend code in <b>{resendTimer}s</b>
                </span>
              ) : (
                <button
                  type="button"
                  className="text-indigo-600 font-semibold hover:underline"
                  onClick={onResend}
                >
                  Resend Code
                </button>
              )}
            </span>
          </div>
        </div>
      </div>
      <div className="w-full max-w-md space-y-4 pb-5 md:pb-0">
        <button
          className={`w-full py-4 md:py-3 rounded-full flex items-center justify-center gap-2 text-white text-xl font-bold transition-all active:scale-[0.98] ${
            isActionLoading
              ? "bg-[#b3b3b3] cursor-wait"
              : value.length === 6
                ? "bg-[#1a1a1a] hover:bg-black shadow-lg"
                : "bg-[#b3b3b3] cursor-not-allowed"
          }`}
          type="submit"
          disabled={isActionLoading || value.length < 6}
        >
          {isActionLoading ? (
            <>
              <Loader2 size={20} className="animate-spin" /> Verifying...
            </>
          ) : (
            <>
              <UserCheck size={20} /> {actionLabel}
            </>
          )}
        </button>
        <button
          type="button"
          className="w-full py-4 md:py-3 rounded-full bg-[#f8faff] text-[#1a1a1a] font-bold flex items-center justify-center gap-2 hover:bg-blue-50"
          onClick={onBack}
        >
          <ArrowLeft size={20} /> Back
        </button>
      </div>
    </form>
  );
};

export default OTPField;
